import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { 
  Building2, 
  TrendingUp, 
  Users, 
  Shield, 
  Clock, 
  CheckCircle,
  BarChart3,
  Star,
  ArrowRight
} from 'lucide-react'
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics'

const ClinicaMedica = () => {
  const { trackButtonClick } = useGoogleAnalytics();

  const handleDemoClick = () => {
    trackButtonClick('agendar_demonstracao', 'clinica_medica');
    window.open('https://calendly.com/cirurgiaautorizada-dev/30min', '_blank');
  };

  const handleConsultantClick = () => {
    trackButtonClick('falar_especialista', 'clinica_medica');
    window.open('https://wa.me/5585982286045', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Building2 className="h-12 w-12 text-blue-300 mr-4" />
                <span className="text-blue-300 text-lg font-medium">Para Clínicas Médicas</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                Sua Clínica no Controle: Gestão Cirúrgica{' '}
                <span className="text-orange-400">Integrada</span> para Resultados Excepcionais
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Otimize processos, eleve a satisfação do paciente e impulsione o crescimento da sua clínica 
                com a Cirurgia Autorizada.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg"
                  onClick={handleDemoClick}
                >
                  Agendar Demonstração
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-blue-700 hover:bg-gray-200 hover:text-blue-900 px-8 py-4 text-lg"
                  onClick={handleConsultantClick}
                >
                  Falar com Especialista
                </Button>
              </div>
            </div>

            {/* Dashboard de Indicadores - Coluna da Direita */}
            <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dashboard de Eficiência</h3>
              
              {/* Gráfico de Evolução */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Evolução Cirúrgica (Últimos 6 meses)</h4>
                <div className="bg-gray-50 rounded-lg p-4 h-32 relative">
                  <div className="flex items-end justify-between h-full">
                    {/* Linha Cirurgias Solicitadas */}
                    <div className="flex flex-col items-center space-y-1">
                      <div className="w-8 bg-blue-500 rounded-t" style={{height: '60px'}}></div>
                      <span className="text-xs text-gray-600">Jan</span>
                    </div>
                    <div className="flex flex-col items-center space-y-1">
                      <div className="w-8 bg-blue-500 rounded-t" style={{height: '75px'}}></div>
                      <span className="text-xs text-gray-600">Fev</span>
                    </div>
                    <div className="flex flex-col items-center space-y-1">
                      <div className="w-8 bg-blue-500 rounded-t" style={{height: '85px'}}></div>
                      <span className="text-xs text-gray-600">Mar</span>
                    </div>
                    <div className="flex flex-col items-center space-y-1">
                      <div className="w-8 bg-blue-500 rounded-t" style={{height: '70px'}}></div>
                      <span className="text-xs text-gray-600">Abr</span>
                    </div>
                    <div className="flex flex-col items-center space-y-1">
                      <div className="w-8 bg-blue-500 rounded-t" style={{height: '90px'}}></div>
                      <span className="text-xs text-gray-600">Mai</span>
                    </div>
                    <div className="flex flex-col items-center space-y-1">
                      <div className="w-8 bg-blue-500 rounded-t" style={{height: '100px'}}></div>
                      <span className="text-xs text-gray-600">Jun</span>
                    </div>
                  </div>
                  
                  {/* Linha Cirurgias Marcadas */}
                  <div className="absolute bottom-8 left-4 right-4 flex items-end justify-between">
                    <div className="w-8 bg-green-500 rounded-t opacity-80" style={{height: '50px'}}></div>
                    <div className="w-8 bg-green-500 rounded-t opacity-80" style={{height: '65px'}}></div>
                    <div className="w-8 bg-green-500 rounded-t opacity-80" style={{height: '75px'}}></div>
                    <div className="w-8 bg-green-500 rounded-t opacity-80" style={{height: '60px'}}></div>
                    <div className="w-8 bg-green-500 rounded-t opacity-80" style={{height: '80px'}}></div>
                    <div className="w-8 bg-green-500 rounded-t opacity-80" style={{height: '85px'}}></div>
                  </div>
                </div>
                <div className="flex justify-center space-x-4 mt-2">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span className="text-xs text-gray-600">Solicitadas</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-xs text-gray-600">Marcadas</span>
                  </div>
                </div>
              </div>

              {/* Boxes de Métricas */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-900">24</div>
                  <div className="text-sm text-blue-700">Cirurgiões</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <Clock className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-900">9.2 dias</div>
                  <div className="text-sm text-green-700">Autorização até Marcação</div>
                </div>
              </div>

              {/* Lista de Hospitais */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Hospitais Mais Utilizados</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Hospital São Lucas</span>
                    <span className="font-semibold text-blue-600">45 cirurgias</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Hospital Santa Maria</span>
                    <span className="font-semibold text-blue-600">32 cirurgias</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Hospital Nossa Senhora</span>
                    <span className="font-semibold text-blue-600">28 cirurgias</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Hospital São José</span>
                    <span className="font-semibold text-blue-600">22 cirurgias</span>
                  </div>
                </div>
              </div>

              {/* Lista de Fornecedores */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Fornecedores Mais Utilizados</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">MedSupply Ltda</span>
                    <span className="font-semibold text-green-600">156 itens</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Hospitalar Express</span>
                    <span className="font-semibold text-green-600">98 itens</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Surgical Pro</span>
                    <span className="font-semibold text-green-600">87 itens</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">MedTech Solutions</span>
                    <span className="font-semibold text-green-600">65 itens</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Transforme Sua Clínica com Gestão Cirúrgica Integrada
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A Cirurgia Autorizada oferece soluções que impactam diretamente a rentabilidade e reputação da sua clínica.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Visibilidade Total dos Processos</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Dashboard Executivo</h4>
                    <p className="text-gray-600">Métricas em tempo real para tomada de decisão estratégica</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Controle de Receita</h4>
                    <p className="text-gray-600">Acompanhamento de valores a receber por procedimento</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Gestão de Equipes</h4>
                    <p className="text-gray-600">Otimização da produtividade e redução de retrabalho</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:pl-8">
              <img 
                src="/images/dashboard-logado.png" 
                alt="Dashboard executivo da Cirurgia Autorizada para clínicas médicas" 
                className="rounded-lg shadow-lg w-full mb-4"
              />
              <p className="text-sm text-gray-500 text-center">
                Calendário executivo com visual moderno e intuitivo para gestão eficiente
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center p-8 border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Maior Rentabilidade</h3>
                <p className="text-gray-600">
                  Redução de custos operacionais e aumento da capacidade de atendimento através da otimização de processos.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Equipe Produtiva</h3>
                <p className="text-gray-600">
                  Eliminação de frustrações e empoderamento da equipe com ferramentas adequadas para excelência no trabalho.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 border-orange-200 bg-orange-50">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Experiência Superior</h3>
                <p className="text-gray-600">
                  Transparência e comunicação proativa que elevam a satisfação do paciente e fortalecem a reputação.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Funcionalidades Específicas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Funcionalidades Específicas Para Clínicas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ferramentas desenvolvidas especificamente para as necessidades de gestão cirúrgica em clínicas médicas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-8">
              <CardContent className="pt-4">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Gestão de Equipes</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Coordene eficientemente toda a equipe médica e administrativa, com controle de produtividade 
                  e distribuição inteligente de tarefas.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Controle de produtividade</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Distribuição de tarefas</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Avaliação de performance</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-8">
              <CardContent className="pt-4">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                    <Shield className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Compliance e Auditoria</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Mantenha registro cuidadoso de todos os passos do processo e quem participou de cada etapa.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Trilha de auditoria</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Conformidade LGPD</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Documentação automática</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Imagem demonstrativa das funcionalidades */}
          <div className="mt-16 text-center">
            <img 
              src="/images/lista-status-guias.png" 
              alt="Lista de cirurgias com diferentes status para gestão completa" 
              className="rounded-lg shadow-lg mx-auto max-w-4xl w-full"
            />
            <p className="text-sm text-gray-500 mt-4">
              Gestão completa de todas as cirurgias com status em tempo real
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronta Para Elevar Sua Clínica ao Próximo Nível?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Descubra como a Cirurgia Autorizada pode transformar a gestão da sua clínica e impulsionar seus resultados.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg"
              onClick={handleDemoClick}
            >
              Agendar Demonstração <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-blue-700 hover:bg-gray-200 hover:text-blue-900 px-8 py-4 text-lg"
                  onClick={handleConsultantClick}
                >
              Falar com Especialista
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ClinicaMedica

